import Icon from "@/components/Icon";
import { toast } from "@/components/Toast";
import { galleryLangRecords, Gallery_Words } from "@/pages/Gallery/lang";
import { WithClassName } from "@/types/base";
import { AIMetaType, getOriginMeta, Img2ImgMeta } from "@/types/meta";
import { genBlock } from "@/utils/bem";
import { parseLabels } from "@/utils/misc";
import { Box, Button, Center, chakra, Divider, Flex, Image, Text, Tooltip } from "@chakra-ui/react";
import { langStore } from "@noli/business";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ReactComponent as CopyIcon } from "@/assets/icons/copy.svg";
import { ReactComponent as CycleIcon } from "@/assets/icons/cycle.svg";
import getScrollBarProps from "@/styles/scrollBarProps";
import WithOriginalPrompt from "@/pages/Gallery/components/GalleryDetail/components/WithOriginalPrompt";
import { parseModelType } from "@/modules/editor/utils/meta";
import { THIRD_PARTY_MODEL_TYPES } from "@/thirdParty/constants";
import { Third_Party_Words } from "@/thirdParty/lang";
import { useTranslate } from "@/utils/lang";
import { NoliboxModelTypes } from "@/modules/editor/constant";

interface MetaJsonLabelsType {
  // 是否是「AI 生成的图片」
  isAI: boolean;
  // 是否是「文字生成图片」
  isTxt2Img: boolean;
  // 是否是「图片转换」
  isImg2Img: boolean;
  // 是否是「循环图样生成」
  isCircular: boolean;
  // 是否是「动漫图片生成」
  isAnime: boolean;
  // 是否是「超分辨率」
  isSuperResolution: boolean;
  // 是否是「图像修补 inpainting」
  isInpainting: boolean;
  // 是否是「合成风景」
  isLandscapeSynthesis: boolean;
  // 是否是「动漫超分辨率」
  isAnimeSuperResolution: boolean;
  // 是不是百度文心
  isWenxin: boolean;
  isScopeReplace: boolean;
}

const block = genBlock("c-image-meta-info");
const ImageMetaInfoComponent: React.FC<
  WithClassName<{
    metaJson: any;
  }>
> = ({ metaJson, className }) => {
  const lang = langStore.tgt;
  const { translate } = useTranslate();
  const _metaJson = useMemo(() => {
    if (typeof metaJson === "string") {
      return JSON.parse(metaJson);
    }
    return metaJson;
  }, [metaJson]);
  const originMetaJson = useMemo(() => getOriginMeta(_metaJson), [_metaJson]);

  const metaJsonLabels = useMemo<MetaJsonLabelsType>(() => {
    const noMetaJson = {
      isAI: false,
      isTxt2Img: false,
      isImg2Img: false,
      isCircular: false,
      isAnime: false,
      isSuperResolution: false,
      isInpainting: false,
      isLandscapeSynthesis: false,
      isAnimeSuperResolution: false,
      isOutpainting: false,
      isScopeReplace: false,
      isWenxin: false,
    };
    return originMetaJson ? parseLabels(originMetaJson) : noMetaJson;
  }, [originMetaJson]);

  const labelKeys = useMemo(() => {
    return Object.keys(metaJsonLabels).filter(
      (key) => metaJsonLabels?.[key as keyof MetaJsonLabelsType] && key !== "isAI",
    );
  }, [metaJsonLabels]);
  const isAI = useMemo(() => metaJsonLabels?.isAI || metaJsonLabels?.isWenxin, [metaJsonLabels]);

  const aiTypeMeta = useMemo(() => originMetaJson as AIMetaType, [originMetaJson]);
  const hasOriginNegativePrompt = useMemo(() => {
    return (
      aiTypeMeta?.data?.negative_prompt &&
      aiTypeMeta?.data?.negative_prompt === aiTypeMeta?.data?.promptInfo?.originalNegative
    );
  }, [aiTypeMeta]);

  const hasOriginPrompt = useMemo(() => {
    return (
      aiTypeMeta?.data?.text && aiTypeMeta?.data?.text === aiTypeMeta?.data?.promptInfo?.original
    );
  }, [aiTypeMeta]);

  const getModelType = (metaJson: any) => {
    const model = parseModelType(metaJson);
    if (!model) return;
    for (const m of THIRD_PARTY_MODEL_TYPES) {
      if (m === model) {
        return translate(Third_Party_Words[m]);
      }
    }
    return galleryLangRecords[lang][`${model as NoliboxModelTypes}ModelTag`];
  };

  return (
    <Flex flexDir="column" className={classNames(className, block())} flex={1} h="100%">
      <Flex
        className={block("prompt-info")}
        flexDirection="column"
        w="100%"
        flex={1}
        bg="bg.panelBg"
        border="1px solid"
        borderColor="#585858"
        rounded="8px"
        p="20px 30px"
      >
        {isAI ? (
          <Flex flexDirection="column" color="white" cursor="text" fontSize="16px">
            <Text
              userSelect="text"
              textAlign="justify"
              maxH="350px"
              overflowY="auto"
              {...getScrollBarProps({ autoMargin: false })}
            >
              {isAI ? (
                <>
                  <WithOriginalPrompt
                    title={galleryLangRecords[lang]["prompt"]}
                    expandContent={
                      hasOriginPrompt ? undefined : aiTypeMeta?.data?.promptInfo?.original
                    }
                    needCopy
                    copyText={galleryLangRecords[lang]["copy"]}
                    expandText={galleryLangRecords[lang]["showOriginal"]}
                    content={aiTypeMeta?.data?.text}
                  />
                  {aiTypeMeta?.data?.negative_prompt && (
                    <WithOriginalPrompt
                      title={galleryLangRecords[lang]["negativePrompt"]}
                      expandContent={
                        hasOriginNegativePrompt
                          ? undefined
                          : aiTypeMeta?.data?.promptInfo?.originalNegative
                      }
                      needCopy
                      copyText={galleryLangRecords[lang]["copy"]}
                      expandText={galleryLangRecords[lang]["showOriginal"]}
                      content={aiTypeMeta?.data?.negative_prompt}
                    />
                  )}
                </>
              ) : (
                galleryLangRecords[lang]["The-image-is-not-generated-by-AI"]
              )}
            </Text>
            {/* <Divider borderColor="text.primary" my="16px" /> */}
            <Flex mt="12px">
              <Text>{galleryLangRecords[lang]["modelType"]}</Text>
              <Flex flexWrap="wrap" gap="10px">
                <Text
                  color="text.disabled"
                  fontSize="12px"
                  minH="25x"
                  p="4px"
                  border="1px solid"
                  borderColor="text.secondary"
                  rounded="4px"
                >
                  {getModelType(originMetaJson)}
                </Text>
              </Flex>
            </Flex>
            <Flex mt="12px">
              <Text>{galleryLangRecords[lang]["handle"]}</Text>
              <Flex flexWrap="wrap" gap="10px">
                {labelKeys.map((key, index) => {
                  if (!metaJsonLabels?.[key as keyof MetaJsonLabelsType]) {
                    return null;
                  }
                  if (key === "model") {
                    return null;
                  }
                  if (key === "version") {
                    return null;
                  }
                  return (
                    <Tooltip
                      key={`promptTextLabelItemNode${index}`}
                      placement="bottom"
                      bg="#5f5f5f"
                      hasArrow={true}
                      isDisabled={key !== "isImg2Img"}
                      label={
                        // 如果是图生图 hover上标签要展示原图
                        (originMetaJson as Img2ImgMeta)?.data?.url ? (
                          <Box w="100px" h="auto">
                            <Text fontSize="14px">{galleryLangRecords[lang]["originalImage"]}</Text>
                            <Image
                              src={(originMetaJson as Img2ImgMeta)?.data?.url ?? ""}
                              w="100%"
                              mt="8px"
                            />
                          </Box>
                        ) : null
                      }
                    >
                      <Text
                        key={`promptTextLabelItemNode${index}`}
                        color="text.disabled"
                        fontSize="12px"
                        minH="25x"
                        p="4px"
                        border="1px solid"
                        borderColor="text.secondary"
                        rounded="4px"
                      >
                        {galleryLangRecords[lang][key as Gallery_Words]}
                      </Text>
                    </Tooltip>
                  );
                })}
              </Flex>
            </Flex>
            {(originMetaJson as any)?.data?.style && (
              <Flex mt="12px">
                <dt>{galleryLangRecords[lang]["configStyle"]}：</dt>
                <Flex flexWrap="wrap" gap="10px">
                  <Text
                    color="text.disabled"
                    fontSize="12px"
                    minH="25x"
                    p="4px"
                    border="1px solid"
                    borderColor="text.secondary"
                    rounded="4px"
                  >
                    {galleryLangRecords[lang]["styleText"].replace(
                      "$",
                      (originMetaJson as any)?.data?.style,
                    )}
                  </Text>
                </Flex>
              </Flex>
            )}
          </Flex>
        ) : (
          <Center
            w="100%"
            h="100%"
            cursor="no-drop"
            userSelect="none"
            fontSize="20px"
            lineHeight="28px"
            color="text.primary"
          >
            {galleryLangRecords[lang]["The-image-is-not-generated-by-AI"]}
          </Center>
        )}
      </Flex>

      <Flex w="100%" justifyContent="space-between" h="100px" pt="26px" gap="12px">
        <CopyToClipboard
          text={(originMetaJson as AIMetaType)?.data?.text}
          onCopy={() => {
            toast({
              status: "success",
              title: galleryLangRecords[lang]["Copied-to-the-clipboard"],
            });
          }}
        >
          <Button flex={1} h="47px" disabled={!isAI}>
            <Icon
              svg={CopyIcon}
              fontSize="20px"
              color="white"
              strokeByCurrentColor
              flexShrink={0}
              mr="5px"
            />
            {galleryLangRecords[lang]["Copy-Prompt"]}
          </Button>
        </CopyToClipboard>
        <CopyToClipboard
          text={JSON.stringify(originMetaJson)}
          onCopy={() => {
            toast({
              status: "success",
              title: galleryLangRecords[lang]["Copied-to-the-clipboard"],
            });
          }}
        >
          <Button flex={1} h="47px" disabled={!isAI}>
            <Icon
              svg={CycleIcon}
              fontSize="20px"
              color="white"
              strokeByCurrentColor
              flexShrink={0}
              mr="5px"
            />
            {galleryLangRecords[lang]["Copy-all-Parameters"]}
          </Button>
        </CopyToClipboard>
      </Flex>
    </Flex>
  );
};

export const ImageMetaInfo = chakra(observer(ImageMetaInfoComponent));
